'use client';
import { useRef, useEffect, useCallback } from 'react';

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

const LineGrid = ({
  lineThickness = 1,
  gap = 50,
  baseColor = "rgba(128,128,128,0.2)",
  proximity = 150,
  className = "",
  style
}) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const dotsRef = useRef([]);

  const buildGrid = useCallback(() => {
      const wrap = wrapperRef.current;
      const canvas = canvasRef.current;
      if (!wrap || !canvas) return;

      const { width, height } = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);

      const buffer = proximity;
      const effectiveWidth = width + buffer * 2;
      const effectiveHeight = height + buffer * 2;

      const cols = Math.floor((effectiveWidth + gap) / (lineThickness + gap));
      const rows = Math.floor((effectiveHeight + gap) / (lineThickness + gap));
      const cell = lineThickness + gap;

      const gridW = cell * cols - gap;
      const gridH = cell * rows - gap;

      const extraX = effectiveWidth - gridW;
      const extraY = effectiveHeight - gridH;

      const startX = extraX / 2 - buffer;
      const startY = extraY / 2 - buffer;

      const dots = [];
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const cx = startX + x * cell;
          const cy = startY + y * cell;
          dots.push({ cx, cy });
        }
      }
      dotsRef.current = dots;
  }, [lineThickness, gap, proximity]);

  useEffect(() => {
    buildGrid();

    const debouncedBuildGrid = debounce(buildGrid, 250);
    window.addEventListener('resize', debouncedBuildGrid);

    return () => {
      window.removeEventListener('resize', debouncedBuildGrid);
    };
  }, [buildGrid]);

  useEffect(() => {
    let rafId;

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const rows = dotsRef.current.length > 0 ? dotsRef.current.filter(d => d.cx === dotsRef.current[0].cx).length : 0;
      const cols = dotsRef.current.length > 0 ? dotsRef.current.length / rows : 0;

      ctx.lineWidth = lineThickness;
      ctx.strokeStyle = baseColor;

      for (let i = 0; i < dotsRef.current.length; i++) {
        const dot = dotsRef.current[i];
        const x = i % cols;
        const y = Math.floor(i / cols);

        if (x < cols - 1) {
          const nextDot = dotsRef.current[i + 1];
          ctx.beginPath();
          ctx.moveTo(dot.cx, dot.cy);
          ctx.lineTo(nextDot.cx, nextDot.cy);
          ctx.stroke();
        }

        if (y < rows - 1) {
          const belowDot = dotsRef.current[i + cols];
          ctx.beginPath();
          ctx.moveTo(dot.cx, dot.cy);
          ctx.lineTo(belowDot.cx, belowDot.cy);
          ctx.stroke();
        }
      }

      rafId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafId);
  }, [baseColor, lineThickness]);

  return (
    <section className={`p-4 flex items-center justify-center h-full w-full absolute inset-0 -z-10 ${className}`} style={style}>
      <div ref={wrapperRef} className="w-full h-full relative">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      </div>
    </section>
  );
};

export default LineGrid;